import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import validator from "validator";
import { createDiscussion, uploadImage } from "../../../../api/action/discussion.jsx";

const DEFAULT_TESTIMONIALS = [];

const EMPTY_FORM = {
  type: "FEEDBACK",
  highLight: "",
  rating: 5,
  recommend: true,
  description: "",
  isAbuse: false,
  active: true,
  featured: false,
  approved: false,
  verified: false,
  displayOrder: "",
  likesCount: "",
  helpfulCount: "",
  productPrimaryId: "",
  createdDate: "",
  createdBy: "",
  modifiedBy: "",
  entityVersion: "",
  tagsText: "",
  author: {
    userId: "",
    firstName: "",
    lastName: "",
    title: "",
    companyOrInstitution: "",
    country: "",
    state: "",
    city: "",
    imageUrl: null,
    email: "",
    socialMediaAccounts: [],
  },
};

const EMPTY_ERRORS = {
  highLight: "",
  description: "",
  rating: "",
  authorFirstName: "",
  authorLastName: "",
  authorEmail: "",
  linkedinProfile: "",
  imageUpload: "",
};

const inputClassName =
  "w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 shadow-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20";

const labelClassName = "block text-sm font-medium text-gray-700 mb-1.5";

const textareaClassName =
  "w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 shadow-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 min-h-[120px]";

const safeParseStoredJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const coerceNumber = (value) => {
  if (value === "" || value === null || value === undefined) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const buildAuthorName = (author = {}) => {
  const fullName = [author.firstName, author.lastName].filter(Boolean).join(" ").trim();
  return fullName || author.userId || "Anonymous";
};

const buildAuthorMeta = (author = {}) =>
  [author.title, author.companyOrInstitution, author.city, author.state, author.country]
    .filter(Boolean)
    .slice(0, 3)
    .join(" • ");

const normalizeTestimonial = (testimonial = {}) => ({
  ...testimonial,
  type: testimonial.type || "FEEDBACK",
  highLight: testimonial.highLight || testimonial.highlight || "",
  rating: Number(testimonial.rating || 5),
  recommend: testimonial.recommend !== false,
  description: testimonial.description || "",
  approved: testimonial.approved || false,
  author: {
    ...testimonial.author,
    imageUrl: testimonial.author?.imageUrl || null,
    socialMediaAccounts: testimonial.author?.socialMediaAccounts || [],
  },
});

const getLinkedInProfile = (socialMediaAccounts = []) => {
  const linkedInAccount = socialMediaAccounts.find(
    account => account.name?.toLowerCase() === "linkedin" || account.name?.toLowerCase() === "linked_in"
  );
  return linkedInAccount?.value || "";
};

const SocialMediaIcons = ({ socialMediaAccounts = [] }) => {
  const getIcon = (name) => {
    const lowerName = name?.toLowerCase() || "";
    if (lowerName === "linkedin" || lowerName === "linked_in") {
      return (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451c.979 0 1.771-.773 1.771-1.729V1.729C24 .774 23.222 0 22.225 0z" />
        </svg>
      );
    }
    return null;
  };

  const linkedInProfile = getLinkedInProfile(socialMediaAccounts);

  if (!linkedInProfile) return null;

  return (
    <div className="flex gap-2 mt-2">
      <a
        href={linkedInProfile}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-gray-500 hover:text-blue-600 transition-colors group"
        title="View LinkedIn Profile"
      >
        {getIcon("linkedin")}
        <span className="text-xs text-gray-500 group-hover:text-blue-600 font-medium">
          LinkedIn
        </span>
      </a>
    </div>
  );
};

// Updated ImageUpload component with API integration using companyFolderId
const ImageUpload = ({ imageUrl, onImageChange, label = "Profile Picture", companyFolderId }) => {
  const [preview, setPreview] = useState(imageUrl?.displayLink || imageUrl || "");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setUploadError("Please upload an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("Image size should be less than 5MB");
      return;
    }

    setUploadError("");
    setIsUploading(true);

    // Create preview URL for immediate display
    const previewUrl = URL.createObjectURL(file);
    setPreview(previewUrl);

    try {
      // Upload image to server with company folder ID
      const response = await uploadImage(file, companyFolderId);

      // Parse the response (assuming it returns the storedLink)
      let storedLink = response;
      if (typeof response === 'string') {
        try {
          const parsed = JSON.parse(response);
          storedLink = parsed.data || parsed.storedLink || response;
        } catch {
          storedLink = response;
        }
      }

      // Create ImageContent object
      const imageContent = {
        storedLink: storedLink,
        displayLink: previewUrl // Use preview URL for display until actual image loads
      };

      // Call the onImageChange callback with the ImageContent object
      onImageChange(imageContent);

      setIsUploading(false);
    } catch {
      setUploadError("Failed to upload image. Please try again.");
      setIsUploading(false);
      // Clear preview on error
      setPreview(imageUrl?.displayLink || imageUrl || "");
    }
  };

  return (
    <div className="mb-4">
      <label className={labelClassName}>{label}</label>
      <div className="flex items-center gap-4">
        {preview && (
          <div className="relative">
            <img
              src={preview}
              alt="Profile preview"
              className="h-16 w-16 rounded-full object-cover border-2 border-gray-200"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://via.placeholder.com/64?text=No+Image";
              }}
            />
          </div>
        )}
        <div className="flex-1">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={isUploading}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {isUploading && (
            <p className="mt-1 text-xs text-blue-600">Uploading...</p>
          )}
          {uploadError && (
            <p className="mt-1 text-xs text-red-600">{uploadError}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const StarRating = ({ rating = 5, onRatingChange, interactive = false }) => {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => interactive && onRatingChange?.(star)}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          className={`${interactive ? "cursor-pointer" : "cursor-default"} transition-all`}
          disabled={!interactive}
        >
          <svg
            className={`h-6 w-6 ${(hoverRating || rating) >= star
              ? "text-yellow-400 fill-current"
              : "text-gray-300 fill-current"
              } ${interactive && "hover:scale-110 transition-transform"}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </button>
      ))}
    </div>
  );
};

const FormField = ({ label, required = false, children, error, hint }) => (
  <div className="mb-4">
    <label className={labelClassName}>
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {children}
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    {hint && !error && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
  </div>
);

const TestimonySection = ({
  testimonials = [],
  companyName = "",
  companyPrimaryId,
}) => {
  const authState = useSelector((state) => state?.auth || {});
  const companyDetails = useSelector((state) => state.companyReducer.company);

  // Get company folder ID from dynamic attributes
  const companyFolderId = useMemo(() => {
    const folderAttr = companyDetails?.dynamicAttributes?.find(
      attr => attr.name === "company_content_folder_id"
    );
    return folderAttr?.value || "default";
  }, [companyDetails]);

  const persistedUser =
    authState?.userInfo ||
    authState?.userDetails ||
    authState?.user ||
    safeParseStoredJson("userInfo") ||
    safeParseStoredJson("userData") ||
    safeParseStoredJson("user");
  const currentUser =
    persistedUser?.userDetails || persistedUser?.user || persistedUser?.profile || persistedUser || {};

  const buildInitialForm = () => ({
    ...EMPTY_FORM,
    type: "FEEDBACK",
    createdBy:
      `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim() ||
      currentUser?.email ||
      "WEB_UI",
    modifiedBy:
      `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim() ||
      currentUser?.email ||
      "WEB_UI",
    author: {
      ...EMPTY_FORM.author,
      userId:
        currentUser?.userId ||
        currentUser?.email ||
        currentUser?.primaryId?.toString?.() ||
        "",
      firstName: currentUser?.firstName || "",
      lastName: currentUser?.lastName || "",
      title: currentUser?.title || "",
      companyOrInstitution: currentUser?.companyOrInstitution || "",
      country: currentUser?.country || "",
      state: currentUser?.state || "",
      city: currentUser?.city || "",
      imageUrl: currentUser?.imageUrl || null,
      email: currentUser?.email || "",
      socialMediaAccounts: currentUser?.socialMediaAccounts || [],
    },
  });

  const [discussionTestimonials, setDiscussionTestimonials] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });
  const [formErrors, setFormErrors] = useState(EMPTY_ERRORS);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(buildInitialForm);
  const [linkedInProfile, setLinkedInProfile] = useState(() => getLinkedInProfile(buildInitialForm().author.socialMediaAccounts));

  const featuredTestimonials = useMemo(() => {
    if (discussionTestimonials.length) {
      return discussionTestimonials.slice(0, 9);
    }
    if (testimonials.length) {
      return testimonials.map(normalizeTestimonial).filter(t => t.approved === true).slice(0, 9);
    }
    return DEFAULT_TESTIMONIALS.map(normalizeTestimonial);
  }, [discussionTestimonials, testimonials]);

  const updateField = (field, value) => {
    setFormData((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const updateAuthorField = (field, value) => {
    setFormData((current) => ({
      ...current,
      author: {
        ...current.author,
        [field]: value,
      },
    }));
  };

  const updateImageUrl = (imageContent) => {
    setFormData((current) => ({
      ...current,
      author: {
        ...current.author,
        imageUrl: imageContent,
      },
    }));
  };

  const validateForm = () => {
    const nextErrors = { ...EMPTY_ERRORS };

    if (!formData.highLight.trim()) {
      nextErrors.highLight = "Highlight is required.";
    }

    if (!formData.description.trim()) {
      nextErrors.description = "Description is required.";
    }

    if (!Number(formData.rating) || Number(formData.rating) < 1 || Number(formData.rating) > 5) {
      nextErrors.rating = "Rating must be between 1 and 5.";
    }

    if (!formData.author.firstName.trim()) {
      nextErrors.authorFirstName = "First name is required.";
    }

    if (!formData.author.lastName.trim()) {
      nextErrors.authorLastName = "Last name is required.";
    }

    if (formData.author.email && !validator.isEmail(formData.author.email)) {
      nextErrors.authorEmail = "Enter a valid email address.";
    }

    if (linkedInProfile && linkedInProfile.trim()) {
      const linkedInPattern = /^https?:\/\/(www\.)?linkedin\.com\/(in|company)\/[\w-]+\/?$/;
      if (!linkedInPattern.test(linkedInProfile.trim())) {
        nextErrors.linkedinProfile = "Enter a valid LinkedIn profile URL (e.g., https://linkedin.com/in/username)";
      }
    }

    setFormErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const resetForm = () => {
    setFormData(buildInitialForm());
    setFormErrors(EMPTY_ERRORS);
    setLinkedInProfile("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: "", message: "" });

    // Create social media accounts array directly
    const socialMediaAccounts = [];
    if (linkedInProfile && linkedInProfile.trim()) {
      socialMediaAccounts.push({
        name: "linkedIn",
        value: linkedInProfile.trim(),
      });
    }

    const authorPayload = {
      userId: formData.author.userId.trim() || undefined,
      firstName: formData.author.firstName.trim(),
      lastName: formData.author.lastName.trim(),
      title: formData.author.title.trim() || undefined,
      companyOrInstitution: formData.author.companyOrInstitution.trim() || undefined,
      country: formData.author.country.trim() || undefined,
      state: formData.author.state.trim() || undefined,
      city: formData.author.city.trim() || undefined,
      imageUrl: formData.author.imageUrl || null,
      socialMediaAccounts: socialMediaAccounts,
    };

    const payload = {
      type: "FEEDBACK",
      highLight: formData.highLight.trim(),
      rating: Number(formData.rating),
      recommend: Boolean(formData.recommend),
      description: formData.description.trim(),
      createdBy: formData.createdBy.trim() || buildAuthorName(authorPayload),
      modifiedBy: formData.modifiedBy.trim() || buildAuthorName(authorPayload),
      entityVersion: coerceNumber(formData.entityVersion),
      createdDate: formData.createdDate
        ? new Date(formData.createdDate).toISOString()
        : new Date().toISOString(),
      author: authorPayload,
      company: companyPrimaryId ? { primaryId: companyPrimaryId } : undefined,
    };

    try {
      const createdDiscussion = await createDiscussion(payload);
      const normalized = normalizeTestimonial(createdDiscussion || payload);

      setDiscussionTestimonials((current) =>
        [normalized, ...current].sort((left, right) => {
          const leftOrder = Number(left.displayOrder || 0);
          const rightOrder = Number(right.displayOrder || 0);
          if (leftOrder !== rightOrder) {
            return leftOrder - rightOrder;
          }
          return new Date(right.createdDate || 0) - new Date(left.createdDate || 0);
        })
      );

      setSubmitStatus({
        type: "success",
        message: "Your feedback has been submitted successfully! It will appear after admin approval.",
      });

      setTimeout(() => {
        setShowForm(false);
        resetForm();
      }, 3000);
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: typeof error === "string" ? error : "Unable to submit feedback right now.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (featuredTestimonials.length === 0 && !showForm) {
    return (
      <section className="bg-gray-50 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900">Testimonials</h2>
          <p className="mt-4 text-gray-600">No approved testimonials available yet.</p>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-all"
          >
            Be the first to share your experience
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-gradient-to-b from-gray-50 to-white py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            What Our Community Says
          </h2>
          <p className="mt-4 text-lg leading-8 text-gray-600 max-w-2xl mx-auto">
            Real experiences from real learners who have transformed their careers
          </p>
        </div>

        {featuredTestimonials.length > 0 && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 mb-16">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
              <div className="text-4xl font-bold text-blue-600">{featuredTestimonials.length}+</div>
              <div className="mt-2 text-sm text-gray-600">Published Testimonials</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
              <div className="text-4xl font-bold text-blue-600">
                {featuredTestimonials.length > 0
                  ? (
                    featuredTestimonials.reduce((total, item) => total + (item.rating || 5), 0) /
                    featuredTestimonials.length
                  ).toFixed(1)
                  : "0"}
              </div>
              <div className="mt-2 text-sm text-gray-600">Average Rating</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
              <div className="text-4xl font-bold text-blue-600">
                {featuredTestimonials.filter(t => t.recommend).length}
              </div>
              <div className="mt-2 text-sm text-gray-600">Would Recommend</div>
            </div>
          </div>
        )}

        <div className="text-center mb-12">
          <button
            type="button"
            onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-all hover:shadow-md"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {showForm ? "Cancel" : "Share Your Experience"}
          </button>
        </div>

        {showForm && (
          <div className="mb-16 bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <h3 className="text-xl font-semibold text-white">Share Your Feedback</h3>
              <p className="text-blue-100 text-sm mt-1">Your story helps others make informed decisions</p>
            </div>

            <div className="p-6">
              {submitStatus.message && (
                <div className={`mb-6 rounded-lg p-4 ${submitStatus.type === "success"
                  ? "bg-green-50 border border-green-200 text-green-800"
                  : "bg-red-50 border border-red-200 text-red-800"
                  }`}>
                  {submitStatus.message}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                  <FormField label="Rating" required error={formErrors.rating}>
                    <div className="pt-2">
                      <StarRating
                        rating={formData.rating}
                        onRatingChange={(val) => updateField("rating", val)}
                        interactive={true}
                      />
                    </div>
                  </FormField>
                </div>

                <FormField label="Highlight / Title" required error={formErrors.highLight}>
                  <input
                    type="text"
                    value={formData.highLight}
                    onChange={(e) => updateField("highLight", e.target.value)}
                    className={inputClassName}
                    placeholder="What stood out most about your experience?"
                  />
                </FormField>

                <FormField label="Your Story" required error={formErrors.description}>
                  <textarea
                    value={formData.description}
                    onChange={(e) => updateField("description", e.target.value)}
                    className={textareaClassName}
                    placeholder="Share your learning journey, achievements, and how this program helped you..."
                    rows={4}
                  />
                </FormField>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">About You</h4>

                  <ImageUpload
                    imageUrl={formData.author.imageUrl}
                    onImageChange={updateImageUrl}
                    label="Profile Picture"
                    companyFolderId={companyFolderId}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField label="First Name" required error={formErrors.authorFirstName}>
                      <input
                        type="text"
                        value={formData.author.firstName}
                        onChange={(e) => updateAuthorField("firstName", e.target.value)}
                        className={inputClassName}
                        placeholder="Enter your first name"
                      />
                    </FormField>

                    <FormField label="Last Name" required error={formErrors.authorLastName}>
                      <input
                        type="text"
                        value={formData.author.lastName}
                        onChange={(e) => updateAuthorField("lastName", e.target.value)}
                        className={inputClassName}
                        placeholder="Enter your last name"
                      />
                    </FormField>

                    <FormField
                      label="Email"
                      error={formErrors.authorEmail}
                      hint="For verification only"
                    >
                      <input
                        type="email"
                        value={formData.author.email}
                        onChange={(e) => updateAuthorField("email", e.target.value)}
                        className={inputClassName}
                        placeholder="your@email.com"
                      />
                    </FormField>

                    <FormField label="Title / Role">
                      <input
                        type="text"
                        value={formData.author.title}
                        onChange={(e) => updateAuthorField("title", e.target.value)}
                        className={inputClassName}
                        placeholder="e.g., Software Engineer, Data Analyst"
                      />
                    </FormField>

                    <FormField label="Company / Institution">
                      <input
                        type="text"
                        value={formData.author.companyOrInstitution}
                        onChange={(e) => updateAuthorField("companyOrInstitution", e.target.value)}
                        className={inputClassName}
                        placeholder="Where do you work or study?"
                      />
                    </FormField>

                    <FormField label="LinkedIn Profile" error={formErrors.linkedinProfile}>
                      <input
                        type="url"
                        value={linkedInProfile}
                        onChange={(e) => setLinkedInProfile(e.target.value)}
                        className={inputClassName}
                        placeholder="https://linkedin.com/in/username"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Enter your full LinkedIn profile URL (e.g., https://linkedin.com/in/johndoe)
                      </p>
                    </FormField>

                    <FormField label="Location">
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={formData.author.city}
                          onChange={(e) => updateAuthorField("city", e.target.value)}
                          className={inputClassName}
                          placeholder="City"
                        />
                        <input
                          type="text"
                          value={formData.author.country}
                          onChange={(e) => updateAuthorField("country", e.target.value)}
                          className={inputClassName}
                          placeholder="Country"
                        />
                      </div>
                    </FormField>
                  </div>
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Feedback"}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-lg border border-gray-300 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-all"
                  >
                    Reset
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {featuredTestimonials.length > 0 && (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {featuredTestimonials.map((testimonial, index) => {
              const authorName = buildAuthorName(testimonial.author);
              const authorMeta = buildAuthorMeta(testimonial.author);
              const authorImage = testimonial.author?.imageUrl?.displayLink ||
                testimonial.author?.imageUrl?.storedLink ||
                testimonial.author?.imageUrl ||
                "";

              return (
                <div
                  key={testimonial.primaryId || `${authorName}-${index}`}
                  className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-gray-200 flex flex-col"
                >
                  <div className="p-6 flex-1">
                    <div className="mb-4">
                      <StarRating rating={testimonial.rating || 5} />
                    </div>

                    {testimonial.highLight && (
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        "{testimonial.highLight}"
                      </h3>
                    )}

                    <p className="text-gray-600 leading-relaxed mb-6 line-clamp-4">
                      "{testimonial.description}"
                    </p>
                  </div>

                  <div className="border-t border-gray-100 pt-4 pb-6 px-6">
                    <div className="flex items-start gap-3">
                      {authorImage ? (
                        <img
                          src={authorImage}
                          alt={authorName}
                          className="h-10 w-10 rounded-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            const fallback = e.target.parentElement?.querySelector('.fallback-avatar');
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className={`h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold fallback-avatar ${authorImage ? 'hidden' : 'flex'}`}>
                        {authorName.charAt(0).toUpperCase()}
                      </div>

                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{authorName}</p>
                        {authorMeta && (
                          <p className="text-sm text-gray-500 mt-1">{authorMeta}</p>
                        )}
                        <SocialMediaIcons socialMediaAccounts={testimonial.author?.socialMediaAccounts} />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                        Feedback
                      </span>
                      {testimonial.recommend && (
                        <span className="inline-flex items-center rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700">
                          Recommended
                        </span>
                      )}
                      {testimonial.verified && (
                        <span className="inline-flex items-center rounded-full bg-purple-50 px-2.5 py-0.5 text-xs font-medium text-purple-700">
                          Verified
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </section>
  );
};

export default TestimonySection;
